/**
 * Webflow Global Styles
 * 
 * This file contains the global Webflow styles extracted from the shared CSS file.
 * These styles are scoped under .ondiem-pro-availability-widget to avoid conflicts
 * with partner site styles.
 */

import { CDN_ASSETS } from '../config';

export const getWebflowGlobalStyles = (): string => {
  return `
/* ============================================
   Webflow Global Styles
   Scoped to .ondiem-pro-availability-widget
   ============================================ */

.ondiem-pro-availability-widget {
  /* CSS Variables from Webflow */
  --figment--background-200: #fffaf5;
  --figment--text-900: #2a3b58;
  --text-color--text-main: var(--figment--text-900);
  --typography--font-900: 2.5rem;
  --typography--line-height-100: 1.2;
  --typography--font-weight-400: 400;
  --typography--font-800: 2rem;
  --typography--font-700: 1.5rem;
  --typography--font-600: 1.25rem;
  --typography--font-weight-600: 600;
  --typography--font-500: 1rem;
  --brand--primary-900: #2b6479;
  --brand--secondary-900: #26576a;
  --typography--font-200: .875rem;
  --typography--font-150: .75rem;
  --text-color--text-alternate: var(--base-color-neutral--white);
  --text-color--text-secondary: var(--base-color-neutral--neutral-darker);
  --text-color--text-primary: var(--base-color-brand--text-dark-blue);
  --base-color-neutral--white: white;
  --figment--cream-background: #fffaf5;
  --typography--font-weight-500: 500;
  --border-color--border-primary: var(--base-color-brand--divider-grey);
  --base-color-brand--text-dark-blue: #26576a;
  --brand--secondary-600: #8c9599;
  --typography--font-weight-300: 300;
  --brand--primary-500: #009abc;
  --brand--secondary-300: #cecece;
  --form-input-border-radius: 10px;
  --typography--font-weight-900: 600;
  --base-color-brand--divider-grey: #cecece;
  --base-color-brand--ondiem-light-blue: #009abc;
  --brand--footer-900: #002c36;
  --background-color--background-secondary: var(--brand--primary-500);
  --background-color--background-tertiary: var(--base-color-brand--pink);
  --text-color--text-error: var(--base-color-system--error-red-dark);
  --base-color-system--hover-state: #235264;
  --figment--background-900: white;
  --background-color--background-warning: var(--base-color-system--warning-yellow);
  --text-color--text-warning: var(--base-color-system--warning-yellow-dark);
  --z-nav: 1000;
  --base-color-brand--dark-blue: #2b6479;
  --text-color--dropdown-nav: var(--brand--secondary-900);
  --brand--secondary-100: #f4f5f6;
  --base-color-brand--ada-blue: #4d89a2;
  --base-color-brand--ada-light-blue: #d4e7ef;
  --brand--accent-150: #dcf1fd;
  --figment--background-300: #fff6ee;
  --figment--background-400: #cce4e9;
  --figment--background-150: #dbf4f3;
  --brand-text-dark-blue: #26576a;
  --base-color-brand--website-background: #f4f5f6;
  --secondary-background-hover: #efefef;
  --base-color-brand--ondiem-light-blue-tint: #dcf1fd;
  --link-color--link-primary: var(--base-color-brand--text-dark-blue);
  --highlight-blue: #00c6f2;
  --base-color-brand--ondiem-lighter-blue: #e6f6ff;
  --accessible-components--dark-grey: #9b9b9b;
  --brand--secondary-150: #efefef;
  --nav-height: 80px;
  --brand--accent-900: #00c6f2;
  --figment--blue-card-border: #7cced1;
  --figment--pink-card-border: #ef89b5;
  --figment--green-border: #7ec9a5;
  --figment--yellow-border: #f0d87c;
  --figment--orange-border: #f9b78e;
  --base-color-brand--text-grey: #838383;
  --brand--accent-500: #61c6ff;
  --brand--accent-300: #d9f5da;
  --modal-scrim: #00000040;
  --border-radius: 20px;
  --blue-200: #dcf1fd;
  --figment--figment-purple: #e2a9f1;
  --brand--text-900: #1f4454;
  --figment--accent-500: #ef89b5;
  --figment--accent-650: #7ec9a5;
  --figment--accent-400: #f0d87c;
  --base-color-neutral--neutral-lighter: #ccc;
  --typography--font-100: .625rem;
  --typography--line-height-900: 3.125;
  --typography--line-height-800: 2.5;
  --typography--line-height-700: 1.875;
  --typography--line-height-600: 1.5;
  --typography--line-height-500: 1.25;
  --typography--line-height-400: 1.25;
  --typography--line-height-300: 1.44;
  --typography--line-height-200: 1.25;
  --typography--line-height-150: 1.2;
  --brand--secondary-800: #235264;
  --brand--accent-100: #eef9ff;
  --brand--footer-800: #0f3942;
  --figment--background-100: #afebec;
  --figment--background-500: #ffd2bd;
  --figment--background-600: #f9dbcd;
  --figment--accent-700: #7cced1;
  --base-color-neutral--neutral-lightest: #eee;
  --base-color-neutral--neutral-light: #aaa;
  --base-color-neutral--neutral: #666;
  --base-color-neutral--neutral-dark: #444;
  --base-color-neutral--neutral-darker: #222;
  --base-color-neutral--neutral-darkest: #111;
  --base-color-system--success-green: #cef5ca;
  --base-color-system--success-green-dark: #114e0b;
  --base-color-system--warning-yellow: #fcf8d8;
  --base-color-system--warning-yellow-dark: #5e5515;
  --base-color-system--error-red: #f8e4e4;
  --base-color-system--error-red-dark: #c20000;
  --background-color--background-success: var(--base-color-system--success-green);
  --z-nav-dropdown: 1100;
  --z-shift-filter: 300;
  --background-color--background-error: var(--base-color-system--error-red);
  --text-color--text-success: var(--base-color-system--success-green-dark);
  --base-color-brand--pink: #e81e75;
  --grey-border: #dfdfdf;
  
  /* Base font settings */
  font-family: Montserrat, sans-serif;
  font-size: 1rem;
  line-height: 1.5;
  color: var(--text-color--text-main);
}

/* Typography Classes */
.ondiem-pro-availability-widget .heading-style-h6 {
  font-size: var(--typography--font-200);
  line-height: 1.5;
  font-weight: var(--typography--font-weight-900);
  flex: none;
  color: var(--text-color--text-main);
  margin-top: 0;
  margin-bottom: 0;
  white-space: nowrap;
}

.ondiem-pro-availability-widget .heading-style-h6.pro_availability-day-card-date {
  border-bottom: 1px solid var(--base-color-brand--divider-grey);
  padding-top: .5rem;
  padding-bottom: .5rem;
}

.ondiem-pro-availability-widget .text-style-link {
  text-decoration: underline;
  color: var(--figment--text-900);
}

.ondiem-pro-availability-widget .text-size-tiny {
  font-size: var(--typography--font-150);
}

.ondiem-pro-availability-widget .text-weight-semibold {
  font-size: 1rem;
  font-weight: var(--typography--font-weight-600);
}

.ondiem-pro-availability-widget .text-weight-semibold.text-size-small {
  font-size: var(--typography--font-200);
}

.ondiem-pro-availability-widget .text-weight-semibold.text-size-tiny {
  font-size: var(--typography--font-150);
}

.ondiem-pro-availability-widget .text-align-center {
  text-align: center;
}

.ondiem-pro-availability-widget .text-weight-medium {
  font-weight: var(--typography--font-weight-500);
}

.ondiem-pro-availability-widget .text-weight-bold {
  font-weight: var(--typography--font-weight-900);
}

/* Spacing Classes */
.padding-global {
  padding-left: 2.5rem;
  padding-right: 2.5rem;
}

.ondiem-pro-availability-widget .padding-bottom {
  padding-top: 0;
  padding-left: 0;
  padding-right: 0;
}

.ondiem-pro-availability-widget .padding-xmedium {
  padding: 1.5rem;
}

.ondiem-pro-availability-widget .padding-bottom.padding-xmedium {
  padding-bottom: 1.5rem;
}

.container-large {
    justify-content: flex-start;
    width: 100%;
    max-width: 75rem;
    margin-left: auto;
    margin-right: auto;
    display: block;
}

/* Form Classes */
.ondiem-pro-availability-widget .form_input {
  border: 1px solid var(--brand--secondary-300);
  border-radius: var(--form-input-border-radius);
  color: var(--figment--text-900);
  background-color: #fff;
  width: 100%;
  min-height: 3rem;
  height: 3rem;
  margin-bottom: 0;
  padding-left: 1rem;
  padding-right: 1rem;
  padding-top: 0;
  padding-bottom: 0;
  font-family: Montserrat, sans-serif;
  font-size: .875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

.ondiem-pro-availability-widget .form_input::placeholder {
  color: var(--figment--text-900);
}

.ondiem-pro-availability-widget .form_input.is-select-input {
  background-image: url("${CDN_ASSETS.SELECT_ARROW}");
  background-position: 95%;
  background-repeat: no-repeat;
  background-size: auto;
  width: 100%;
  min-height: 3rem;
  height: 3rem;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
  -webkit-appearance: none;
  appearance: none;
  box-sizing: border-box;
}

.ondiem-pro-availability-widget .form_input.with-icon {
  background-image: url("${CDN_ASSETS.MAP_ICON}");
  background-position: 12px;
  background-repeat: no-repeat;
  background-size: auto;
  margin-bottom: 0;
  padding-left: 2.5rem;
  padding-top: 0;
  padding-bottom: 0;
  box-sizing: border-box;
}

.ondiem-pro-availability-widget .form_input.is-square {
  border-radius: 10px;
}

.ondiem-pro-availability-widget .form_field-group {
  grid-column-gap: .75rem;
  grid-row-gap: .75rem;
  align-items: center;
  width: 100%;
  display: flex;
}

.ondiem-pro-availability-widget .w-input,
.ondiem-pro-availability-widget .w-select {
  color: var(--figment--text-900);
  vertical-align: middle;
  background-color: #fff;
  border: 1px solid #ccc;
  width: 100%;
  min-height: 3rem;
  height: 3rem;
  margin-bottom: 10px;
  padding: 0 12px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  line-height: 1.42857;
  display: block;
  box-sizing: border-box;
}

.ondiem-pro-availability-widget .w-input:focus,
.ondiem-pro-availability-widget .w-select:focus {
  border-color: var(--brand--primary-900);
  outline: 0;
}



/* Button Classes */
.ondiem-pro-availability-widget .button {
  background-color: var(--text-color--text-main);
  color: var(--base-color-neutral--white);
  text-align: center;
  white-space: nowrap;
  border-radius: 8rem;
  width: 100%;
  padding: .75rem 1.5rem;
  font-weight: 600;
  border: 0;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
}

.ondiem-pro-availability-widget .button.is-text {
  color: var(--text-color--text-main);
  background-color: #0000;
}

.ondiem-pro-availability-widget .button.is-text:hover {
  text-decoration: underline;
}

.ondiem-pro-availability-widget .button.is-left-icon {
  grid-column-gap: .7rem;
  grid-row-gap: .7rem;
  align-items: center;
  display: flex;
}

.ondiem-pro-availability-widget .button.is-xsmall {
  font-size: .625rem;
}

.ondiem-pro-availability-widget .button.is-centered {
  justify-content: center;
}

.ondiem-pro-availability-widget .padding-0 {
  padding: 0;
}

.ondiem-pro-availability-widget .w-inline-block {
  max-width: 100%;
  display: inline-block;
}

/* List Classes */
.ondiem-pro-availability-widget .w-list-unstyled {
  padding-left: 0;
  list-style: none;
}

/* Other Utility Classes */
.ondiem-pro-availability-widget .w-embed:before,
.ondiem-pro-availability-widget .w-embed:after {
  content: " ";
  grid-area: 1 / 1 / 2 / 2;
  display: table;
}

.ondiem-pro-availability-widget .w-embed:after {
  clear: both;
}

.ondiem-pro-availability-widget .w-form-done {
  text-align: center;
  background-color: #ddd;
  padding: 20px;
  display: none;
}

.ondiem-pro-availability-widget .w-form-fail {
  background-color: #ffdede;
  margin-top: 10px;
  padding: 10px;
  display: none;
}

/* Select appearance override */
.ondiem-pro-availability-widget select {
  font-family: Montserrat, sans-serif;
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
}

/* Responsive adjustments for typography */
@media screen and (max-width: 991px) {
  .ondiem-pro-availability-widget {
    --typography--font-900: 2rem;
    --typography--font-800: 1.75rem;
    --typography--font-500: 1.125rem;
    --nav-height: 48px;
  }
  
  .ondiem-pro-availability-widget .heading-style-h6 {
    font-size: 1rem;
  }
  
  .padding-global {
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  }
}

@media screen and (max-width: 767px) {
  .ondiem-pro-availability-widget {
    --nav-height: 48px;
  }
  
  .ondiem-pro-availability-widget .heading-style-h6 {
    font-size: .75rem;
  }
  
  .padding-global {
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  }
}
`;
};
